// ════════════════════════════════════════════════════════════════════════════
//  ManDana — adoption: the data that predates rooms joins its owner
//  ---------------------------------------------------------------------------
//  Storage is compartmented by account now (md-storage.js, "one room per
//  account"). Every device that ran an earlier build holds a pile of bare
//  `md:*` keys that belong to no account at all, because when they were written
//  storage belonged to the DEVICE. Once a room is selected those keys are
//  invisible: the app stands in `md#<account>:` and reads an empty house.
//
//  So the first account to sign in on a device adopts them. That is a real
//  claim — somebody's records change hands once, permanently — and it is the
//  right one, because before this phase those records already belonged to
//  whoever could open the app on this device, and signing in is a strictly
//  stronger proof of that than opening it was.
//
//  ── THE MOVE MUST NEVER LOSE ───────────────────────────────────────────────
//  Copy, read the copy back, compare it, and only then remove the original.
//  Per key, following mdBoardMigrate (md-board.ts:614) rather than mdMigrate's
//  whole-set audit: localStorage offers no transaction, so the smallest unit
//  that can be made safe is one key, and per-key means an interruption leaves
//  every key either wholly moved or wholly untouched.
//
//  A failure leaves the original where it is AND writes no marker, which is
//  what makes "it will be retried" true rather than hopeful. The cost of a
//  failed adoption is a session that reads a partly-populated room while the
//  rest of the data sits in the legacy space — bad, and unavoidable without a
//  transaction, but recoverable on the next boot and never a loss.
//
//  ── THE DATABASES ARE RECORDED, NOT MOVED ──────────────────────────────────
//  Drawings, images and manifests live in IndexedDB, and there can be
//  gigabytes of them. Copying that between two IndexedDB databases at boot is
//  a long, interruptible, quota-bound operation with no user-visible benefit,
//  so the adopter simply KEEPS the bare database names, forever, and every
//  later account on the device gets suffixed ones. Which of the two an account
//  has is written down in its room (`md:compartment`) and never re-decided: a
//  different answer tomorrow is a device that cannot find its own drawings.
//
//  `dbowner:v1` is the device-level claim on those bare names, and it sits
//  outside `md:` on purpose — beside `session:v1`, `device:v1`, `account:v1`.
//  It is a fact about this device's databases, not about any account's data,
//  so it must survive a room being locked and must never appear in a backup.
//  It is claimed by the first account to boot here whether or not there was
//  anything to adopt, because the bare databases exist from the first drawing.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MARKER = 'md:compartment';   // logical: lands in the room it describes
  const DBOWNER = 'dbowner:v1';      // device-level: who holds the bare names

  type Idb = 'legacy' | 'scoped';

  /* Read once per page load and then remembered, because every database name
     in the app asks this question and the answer cannot change after boot. */
  let known: Idb | null = null;

  function marker(): any {
    const m = g.mdRead(MARKER, null);
    return (m && typeof m === 'object') ? m : null;
  }

  /**
   * Which IndexedDB and engine names this account's data lives under.
   *
   * Defaults to 'legacy' — the bare names — when there is no marker to read.
   * That is the identity default the whole compartment scheme rests on: a
   * device where nobody has ever signed in must behave exactly as it did
   * before rooms existed, and so must every test that seeds storage directly.
   */
  function mdCompartmentIdb(): Idb {
    if (known !== null) return known;
    const m = marker();
    return (m && m.idb === 'scoped') ? 'scoped' : 'legacy';
  }

  /** Forget the remembered answer. Only a test, and only between page loads. */
  function mdAdoptForget(): void { known = null; }

  /**
   * Give this device's account-less data an owner, and record which database
   * names that account uses. Run inside mdAppBoot after the room is selected
   * and before hydration — it moves the very keys the map is built from.
   *
   * Never throws: a boot must not die here. Every failure comes back as a
   * value, and no marker is written for any of them.
   */
  async function mdAdopt(accountId: string | null): Promise<any> {
    /* No account is not a failure, it is the front door. And with no room
       selected the legacy space is the room — see mdLegacyKeys(). */
    if (typeof accountId !== 'string' || !accountId) {
      return { ok: true, adopted: false, moved: 0, failed: 0, skipped: 0, reason: 'no_account', idb: mdCompartmentIdb() };
    }

    try {
      const had = marker();
      if (had && had.adopted === true) {
        known = had.idb === 'scoped' ? 'scoped' : 'legacy';
        return { ok: true, adopted: false, moved: 0, failed: 0, skipped: 0, reason: 'already', idb: known };
      }

      /* Claim the bare database names, or discover they are taken. Done before
         the move, because it also decides whether there is a move to make. */
      const owner = g.mdRead(DBOWNER, null);
      const ownerId = (owner && typeof owner === 'object' && typeof owner.id === 'string') ? owner.id : null;
      let idb: Idb;
      if (ownerId === null) {
        g.mdWrite(DBOWNER, { v: 1, id: accountId });
        idb = 'legacy';
      } else {
        idb = ownerId === accountId ? 'legacy' : 'scoped';
      }

      let moved = 0, failed = 0, skipped = 0;

      /* ONE ACCOUNT ADOPTS, AND IT IS THE ONE HOLDING THE BARE NAMES. By the
         time a second account signs in the legacy space is normally already
         empty, so this gate rarely fires — which is exactly why it is a gate
         and not a comment. "Only one account ever adopts" should be a property
         of the code, not a consequence of the order people signed in. */
      if (idb === 'legacy') {
        for (const key of g.mdLegacyKeys()) {
          const raw = g.mdLegacyReadRaw(key);
          if (raw == null) continue;

          const there = g.mdReadRaw(key);

          /* Byte for byte what we were about to write: the copy landed on an
             earlier run and only the removal was left. Finish it. A retry that
             declined here would leave the legacy key on the device for ever,
             and "it will be retried" would be a sentence with no end. */
          if (there === raw) { g.mdLegacyRemove(key); moved += 1; continue; }

          /* Different bytes under the same name. Either two histories genuinely
             collide, or the room has moved on since a partial adoption. Both
             answers are the same: the room's copy is the one the app has been
             reading and writing since, the legacy one is older by definition,
             and NEITHER is deleted. Nothing here is allowed to pick a winner
             by destroying the loser. */
          if (there != null) { skipped += 1; continue; }

          if (!g.mdWriteRaw(key, raw)) { failed += 1; continue; }
          /* The read-back is the whole point. A write that reported success
             and stored something else — a truncating browser, a quota that
             half-wrote — must not be followed by a removal. */
          if (g.mdReadRaw(key) !== raw) { failed += 1; continue; }
          g.mdLegacyRemove(key);
          moved += 1;
        }
      }

      if (failed > 0) {
        /* Deliberately no marker: the next boot repeats this, and until one
           passes cleanly the originals are all still there. */
        return { ok: false, adopted: false, moved, failed, skipped, reason: 'incomplete', idb };
      }

      known = idb;
      g.mdWrite(MARKER, { v: 1, adopted: true, idb, at: Date.now() });
      return { ok: true, adopted: moved > 0, moved, failed, skipped, reason: 'adopted', idb };
    } catch (err: any) {
      return {
        ok: false, adopted: false, moved: 0, failed: 0, skipped: 0,
        error: (err && err.message) || String(err), idb: mdCompartmentIdb(),
      };
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  //  Naming the databases themselves — and taking one apart
  //  ────────────────────────────────────────────────────────────────────────
  //  mdCompartmentIdb() above answers WHICH set of names an account's data
  //  lives under. Everything else in the app that opens a database calls one
  //  of the two functions below instead of asking that question itself.
  //  mdCompartmentDbName turns a bare name into the one this account should
  //  actually open. mdCompartmentDestroy is its undo: the one place that
  //  removes an account's data from a device rather than merely finding it,
  //  for when an account is deleted and asked to leave no trace behind.
  // ════════════════════════════════════════════════════════════════════════

  /* Every IndexedDB database a device-local account can hold data in.
     Named here rather than imported — these are classic scripts with no
     shared module graph (see md-boarddb.js, md-file.ts, md-mstore.ts,
     md-versions.ts) — so this list is kept in step with their DB_NAME
     constants by hand. A database added to the app without a matching line
     here is one mdCompartmentDestroy never learns about, and whatever an
     account left in it survives every deletion, for ever.

     THAT USED TO BE A COMMENT ASKING TO BE REMEMBERED. It is now a test:
     tests/adoption.test.js reads every `DB_NAME` literal in src/app and fails
     if one is missing from this line. The failure it prevents is silent in the
     worst direction — one account reading the drafts and files another account
     cached — so it should not have depended on somebody noticing.

     A name here that no module opens yet is safe: dropping a database that was
     never created succeeds in every browser. mandana-blobs is listed ahead of
     the module that opens it (1.5) deliberately, because the cost of listing
     it early is one no-op and the cost of listing it late is permanent. */
  const IDB_BASES = [
    'mandana-boards', 'mandana-files', 'mandana-manifests',
    'mandana-versions', 'mandana-blobs',
  ];

  /* ── what separates a base name from an account id, and why it is not '#' ──
     The storage seam scopes its KEYS with '#' — `md#<id>:settings` — and
     reusing that character here read as one convention rather than two. It is
     not one convention, because a localStorage key and a database name are
     not the same kind of string.

     SQLite parses a database filename as a URI. '#' begins a fragment and '?'
     begins a query, so `/mandana#<id>.sqlite3` is not the path the OPFS pool
     was told to associate with a file: the schema is built against one thing
     and the app then uses another. Every scoped account's engine open failed
     with `no such table: main.search`, on every browser with OPFS.

     It was invisible because a failed open is a DEGRADED boot and not a
     crash — md-boot.js says so in as many words — so a second account on a
     device quietly had no durable store and no search, and nothing anywhere
     said the word "engine".

     '_' has no meaning in a URI, and it cannot make one base name collide
     with another: account ids are opaque hex, so `mandana` + '_' + <hex>
     can never spell `mandana-boards` + '_' + <hex>. tests/adoption.test.js
     pins the RULE — no URI metacharacter — rather than this character, so
     the next person to reach for ':' is stopped by a test and not by luck. */
  const DB_SEP = '_';

  /**
   * The database name this account's data actually lives under, given a bare
   * base name like 'mandana-boards'.
   *
   * THE DEFAULT IS IDENTITY, twice over. `mdCompartmentIdb()` already answers
   * 'legacy' for a device nobody has ever signed into (see its own comment
   * above), and even a 'scoped' account gets the bare name back when no room
   * is selected yet — a call that runs before mdStorageCompartment(id), or a
   * test that seeds storage directly without ever choosing a room. That is
   * the same property the storage seam itself rests on: hundreds of existing
   * tests, and every device that has never signed in, must see the exact byte
   * string they always have.
   *
   * The suffix is the account's FULL id, never a slice of it. These ids are
   * opaque random hex, and an 8-character slice is only 32 bits of it — a
   * collision budget nobody should spend on a bug whose cost is two accounts
   * silently sharing a database.
   */
  function mdCompartmentDbName(base: string): string {
    if (mdCompartmentIdb() !== 'scoped') return base;
    const id = typeof g.mdStorageCompartmentId === 'function' ? g.mdStorageCompartmentId() : null;
    return (typeof id === 'string' && id) ? `${base}${DB_SEP}${id}` : base;
  }

  /* indexedDB.deleteDatabase returns an IDBOpenDBRequest, not a promise — this
     is the only place that translates it. `onblocked` fires when another tab
     still has the database open: the delete has not happened, and will not
     until that tab closes or navigates away, so this resolves `false` rather
     than `true`. That is not inventing a failure — reporting it as success
     would be the actual lie, because the data the caller asked gone is still
     sitting right there. */
  function dropDatabase(name: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!g.indexedDB || typeof g.indexedDB.deleteDatabase !== 'function') { resolve(false); return; }
      let req: any;
      try {
        req = g.indexedDB.deleteDatabase(name);
      } catch (err) {
        resolve(false);
        return;
      }
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    });
  }

  /**
   * Take one account's data off this device: its room in localStorage, its
   * three IndexedDB databases, and — only when this account is the one
   * holding the bare names — the legacy key space, the three bare databases,
   * and the claim itself, because that account's data IS the legacy pile and
   * with it gone the bare names are free for whoever signs in next.
   *
   * NEVER THROWS, AND NEVER STOPS AT THE FIRST FAILURE. A deletion that gives
   * up halfway leaves somebody's data on a device they asked to have it
   * removed from, which is the one outcome this exists to rule out — so every
   * database drop is attempted independently, every failure is collected
   * rather than raised, and the localStorage wipe runs no matter how the
   * database drops went.
   *
   * WHAT THIS DOES NOT TOUCH. The engine's own copy of this account's
   * documents: the SQLite file it keeps at `/<name>.sqlite3` inside the
   * `md-<name>` OPFS pool (md-engine-worker.js:91,104), or, on a browser that
   * fell back to the memory-idb engine, that same file resting as a blob keyed
   * by `name` inside the shared `mandana-engine` database. Left alone
   * deliberately, not by oversight: it is bounded (the engine holds a mirror
   * of the very documents this function just removed, nothing more), and
   * closing it needs a `destroy` verb in the engine worker that this story
   * does not add. It is a real remnant of a deleted account, not a handled
   * case — said so plainly rather than pretended away.
   */
  async function mdCompartmentDestroy(id: string): Promise<{ ok: boolean; wiped: number; dropped: string[]; failed: string[] }> {
    if (typeof id !== 'string' || !id) return { ok: false, wiped: 0, dropped: [], failed: [] };

    let legacy = false;
    try {
      const owner = g.mdRead(DBOWNER, null);
      legacy = !!(owner && typeof owner === 'object' && owner.id === id);
    } catch (err) {
      /* Could not learn who holds the bare names. Guessing 'yes' risks wiping
         a legacy pile that belongs to nobody being deleted; guessing 'no'
         only risks leaving that pile for a retry. The recoverable guess is
         the one made here. */
      legacy = false;
    }

    let wiped = 0;
    try {
      wiped = g.mdCompartmentWipe(id, { legacy }) || 0;
    } catch (err) {
      /* mdCompartmentWipe's own contract is that it never throws; this guard
         is not distrust of that so much as this function's own promise, which
         is stronger still — nothing in mdCompartmentDestroy may throw, ever,
         for any reason. */
    }

    /* And the claim on the bare names goes with the account that held it. The
       pile those names described was this account's data, and it has just been
       removed — so leaving the claim behind would retire the bare names for the
       life of the device, on the strength of a claim nobody is left to make.
       Every later account would be told 'scoped' by an account that no longer
       exists, and the next person to sign in on a device with nothing on it
       would be in exactly the position this one was.

       Released, never reassigned: this function knows which account is leaving
       and nothing at all about who might arrive. The next mdAdopt claims it. */
    if (legacy) {
      try { g.mdRemove(DBOWNER); } catch (err) { /* a retry will find it again */ }
    }

    const names = legacy ? IDB_BASES : IDB_BASES.map((base) => `${base}${DB_SEP}${id}`);
    const dropped: string[] = [];
    const failed: string[] = [];
    for (const name of names) {
      let ok = false;
      try { ok = await dropDatabase(name); } catch (err) { ok = false; }
      (ok ? dropped : failed).push(name);
    }

    /* ── the engine's resting places (AC-6b) ─────────────────────────────────
       The loop above drops the databases this module knows by name. The
       ENGINE's files — the .sqlite3 in its OPFS pool, the blob in its
       fallback vault — hold every document the account ever wrote, and only
       the engine can demolish them: a live worker holds sync access handles
       on the pool, and nothing else knows where the fallback rests. Asked
       under the same name AC-6 opened it with — the bare 'mandana' when this
       account holds the bare names, the suffixed one otherwise. A page that
       never loaded the engine module never opened a database on it, so there
       is nothing to remove and nothing to report. */
    const engine = legacy ? 'mandana' : `mandana${DB_SEP}${id}`;
    if (typeof g.mdEngineDestroy === 'function') {
      let res: { ok?: boolean } | null = null;
      try { res = await g.mdEngineDestroy(engine); } catch (err) { res = null; }
      (res && res.ok === true ? dropped : failed).push(`engine:${engine}`);
    }

    return { ok: failed.length === 0, wiped, dropped, failed };
  }

  Object.assign(g, {
    mdAdopt,
    mdCompartmentIdb,
    mdAdoptForget,
    mdCompartmentDbName,
    mdCompartmentDestroy,
    MD_COMPARTMENT_MARKER: MARKER,
    MD_DBOWNER_KEY: DBOWNER,
  });
})(typeof window !== 'undefined' ? window : globalThis);
